import { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabase'
import Topbar from '../components/Topbar'
import { useAuth } from '../context/AuthContext'
import { useNavigate, useLocation } from 'react-router-dom'
import { useToast } from '../context/ToastContext'
import { useTranslation } from '../i18n/LanguageContext'
import POSSettingsTab from '../components/pos/POSSettings'
import TaraSettingsTab from '../components/TaraSettingsTab'

// Design system — CSS vars throughout

const COUNTRY_CODES = [
  { code: '+880', country: 'Bangladesh', flag: '🇧🇩', digits: 10 },
  { code: '+91',  country: 'India',      flag: '🇮🇳', digits: 10 },
  { code: '+61',  country: 'Australia',  flag: '🇦🇺', digits: 9  },
  { code: '+1',   country: 'USA/Canada', flag: '🇺🇸', digits: 10 },
  { code: '+44',  country: 'UK',         flag: '🇬🇧', digits: 10 },
  { code: '+65',  country: 'Singapore',  flag: '🇸🇬', digits: 8  },
  { code: '+60',  country: 'Malaysia',   flag: '🇲🇾', digits: 9  },
  { code: '+971', country: 'UAE',        flag: '🇦🇪', digits: 9  },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦', digits: 9 },
  { code: '+49',  country: 'Germany',    flag: '🇩🇪', digits: 11 },
  { code: '+33',  country: 'France',     flag: '🇫🇷', digits: 9  },
  { code: '+81',  country: 'Japan',      flag: '🇯🇵', digits: 10 },
  { code: '+86',  country: 'China',      flag: '🇨🇳', digits: 11 },
  { code: '+92',  country: 'Pakistan',   flag: '🇵🇰', digits: 10 },
  { code: '+94',  country: 'Sri Lanka',  flag: '🇱🇰', digits: 9  },
  { code: '+977', country: 'Nepal',      flag: '🇳🇵', digits: 10 },
  { code: '+20',  country: 'Egypt',      flag: '🇪🇬', digits: 10 },
  { code: '+234', country: 'Nigeria',    flag: '🇳🇬', digits: 10 },
  { code: '+27',  country: 'South Africa', flag: '🇿🇦', digits: 9 },
  { code: '+55',  country: 'Brazil',     flag: '🇧🇷', digits: 11 },
  { code: '+7',   country: 'Russia',     flag: '🇷🇺', digits: 10 },
  { code: '+82',  country: 'South Korea', flag: '🇰🇷', digits: 10 },
  { code: '+62',  country: 'Indonesia',  flag: '🇮🇩', digits: 11 },
  { code: '+63',  country: 'Philippines', flag: '🇵🇭', digits: 10 },
  { code: '+66',  country: 'Thailand',   flag: '🇹🇭', digits: 9  },
  { code: '+84',  country: 'Vietnam',    flag: '🇻🇳', digits: 9  },
  { code: '+98',  country: 'Iran',       flag: '🇮🇷', digits: 10 },
  { code: '+90',  country: 'Turkey',     flag: '🇹🇷', digits: 10 },
  { code: '+34',  country: 'Spain',      flag: '🇪🇸', digits: 9  },
  { code: '+39',  country: 'Italy',      flag: '🇮🇹', digits: 10 },
]

const COUNTRY_FLAGS = {
  Bangladesh: '🇧🇩', Australia: '🇦🇺', 'United States': '🇺🇸',
  'United Kingdom': '🇬🇧', India: '🇮🇳', Singapore: '🇸🇬',
  Malaysia: '🇲🇾', UAE: '🇦🇪', Pakistan: '🇵🇰',
  'Saudi Arabia': '🇸🇦', Germany: '🇩🇪', France: '🇫🇷',
  Japan: '🇯🇵', China: '🇨🇳', 'Sri Lanka': '🇱🇰',
  Nepal: '🇳🇵', Canada: '🇨🇦',
}

const CATEGORIES = [
  'Clothing','Shoes & Sneakers','Leather Items & Bags','Watches & Accessories',
  'Sunglasses','Perfumes','Jewellery','Home Decor','Furniture',
  'Electronics & Appliances','Mobile & Gadgets','Construction Materials',
  'Food & Catering','Organic Foods','Herbal Products','Grocery Items',
  'Medicines','Pet Food & Supplies','Plants, Trees & Seeds','Flowers & Gift Items',
  'Toys & Games','Arts & Crafts','Books','Stationeries','Online Courses',
  'Online Subscriptions & Gift Cards','Vehicles','Land & Property',
  'Beauty & Skincare','Sports & Fitness','Other',
]

const ACTIVITY_LABELS = {
  status_changed: 'Status updated', order_edited: 'Order edited',
  order_deleted: 'Order deleted', dash_order_created: 'Dash order created',
  product_edited: 'Product updated', product_deleted: 'Product deleted',
  stock_updated: 'Stock updated',
}

const ACTIVITY_COLORS = {
  status_changed: 'var(--primary)', order_edited: '#7C3AED', order_deleted: '#DC2626',
  dash_order_created: '#D97706', product_edited: '#16A34A',
  product_deleted: '#DC2626', stock_updated: '#F59E0B',
}

const ITEMS_PER_PAGE = 20

function Icon({ path, size = 15, color = 'var(--text-muted)', strokeWidth = 1.8 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  )
}

const IC = {
  search: 'M21 21l-4.35-4.35 M11 19a8 8 0 100-16 8 8 0 000 16z',
  x:      'M6 18L18 6M6 6l12 12',
  check:  'M5 13l4 4L19 7',
  lock:   'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
  chevL:  'M15 18l-6-6 6-6',
  chevR:  'M9 18l6-6-6-6',
  trash:  'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  api:    'M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  eye:    'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z',
  eyeOff: 'M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24 M1 1l22 22',
}

const inputStyle = (focused) => ({
  width: '100%', border: '1px solid ' + (focused ? 'var(--primary)' : 'var(--border)'), borderRadius: 'var(--radius-md)',
  padding: '9px 12px', fontSize: '12.5px', outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)',
  fontFamily: 'var(--font-ui)', boxSizing: 'border-box', transition: 'border-color 0.15s',
})

function FieldInput({ label, value, onChange, placeholder, type = 'text', optional, disabled, hint }) {
  const [focused, setFocused] = useState(false)
  const [showPass, setShowPass] = useState(false)
  const isPass = type === 'password'
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
        <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)' }}>
          {label}{optional && <span style={{ fontWeight: 400, color: '#B0B3B8', marginLeft: '4px' }}>optional</span>}
        </label>
      </div>
      <div style={{ position: 'relative' }}>
        <input type={isPass && showPass ? 'text' : type} value={value} onChange={e => onChange(e.target.value)}
          placeholder={placeholder} disabled={disabled}
          style={{ ...inputStyle(focused), opacity: disabled ? 0.6 : 1, paddingRight: isPass ? '36px' : '12px' }}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
        {isPass && (
          <button type="button" onClick={() => setShowPass(s => !s)}
            style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}>
            <Icon path={showPass ? IC.eyeOff : IC.eye} color={'var(--text-muted)'} size={14} />
          </button>
        )}
      </div>
      {hint && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>{hint}</div>}
    </div>
  )
}

function FieldTextarea({ label, value, onChange, placeholder, optional, rows = 2 }) {
  const [focused, setFocused] = useState(false)
  return (
    <div>
      <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
        {label}{optional && <span style={{ fontWeight: 400, color: '#B0B3B8', marginLeft: '4px' }}>optional</span>}
      </label>
      <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows}
        style={{ ...inputStyle(focused), resize: 'none' }}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
    </div>
  )
}

function PhoneField({ label, countryCode, phone, onCountryCodeChange, onPhoneChange, optional, searchPh, noResults, digitHint1, digitHint2 }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [focused, setFocused] = useState(false)
  const ref = useRef()
  const selected = COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0]
  const maxDigits = selected.digits
  const filtered  = COUNTRY_CODES.filter(c => c.country.toLowerCase().includes(search.toLowerCase()) || c.code.includes(search))

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler); return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handlePhoneChange = (val) => { const digits = val.replace(/\D/g, '').slice(0, maxDigits); onPhoneChange(digits) }

  return (
    <div>
      <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
        {label}{optional && <span style={{ fontWeight: 400, color: '#B0B3B8', marginLeft: '4px' }}>optional</span>}
      </label>
      <div style={{ display: 'flex', gap: '8px' }}>
        <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
          <button type="button" onClick={() => setOpen(o => !o)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 10px', borderRadius: 'var(--radius-md)', border: '1px solid ' + (open ? 'var(--primary)' : 'var(--border)'), background: 'var(--bg-card)', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '12.5px', color: 'var(--text-primary)' }}>
            <span>{selected.flag}</span>
            <span style={{ fontWeight: 600 }}>{selected.code}</span>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={'var(--text-muted)'} strokeWidth="2.5"><path d="M6 9l6 6 6-6" /></svg>
          </button>
          {open && (
            <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, width: '240px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 100, overflow: 'hidden' }}>
              <div style={{ padding: '8px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ position: 'relative' }}>
                  <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder={searchPh || 'Search country...'}
                    style={{ ...inputStyle(true), padding: '7px 10px 7px 30px', fontSize: '12px' }} />
                  <div style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)' }}><Icon path={IC.search} color={'var(--text-muted)'} size={13} /></div>
                </div>
              </div>
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {filtered.map(c => (
                  <button key={c.code} type="button" onClick={() => { onCountryCodeChange(c.code); setOpen(false); setSearch('') }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', background: c.code === countryCode ? 'var(--primary-light)' : 'var(--bg-card)', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'background 0.1s' }}
                    onMouseEnter={e => { if (c.code !== countryCode) e.currentTarget.style.background = 'var(--bg-muted)' }}
                    onMouseLeave={e => { if (c.code !== countryCode) e.currentTarget.style.background = 'var(--bg-card)' }}>
                    <span style={{ fontSize: '16px' }}>{c.flag}</span>
                    <span style={{ fontSize: '12.5px', color: 'var(--text-primary)', flex: 1 }}>{c.country}</span>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: c.code === countryCode ? 'var(--primary)' : 'var(--text-muted)' }}>{c.code}</span>
                  </button>
                ))}
                {filtered.length === 0 && <div style={{ padding: '16px', textAlign: 'center', fontSize: '12px', color: 'var(--text-muted)' }}>{noResults || 'No results'}</div>}
              </div>
            </div>
          )}
        </div>
        <div style={{ flex: 1 }}>
          <input type="tel" value={phone} onChange={e => handlePhoneChange(e.target.value)}
            placeholder={'e.g. ' + '1'.repeat(Math.min(maxDigits, 5)) + '... (' + maxDigits + ' digits)'}
            maxLength={maxDigits} style={inputStyle(focused)}
            onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
        </div>
      </div>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
        {selected.country} {digitHint1 || 'numbers are'} {maxDigits} {digitHint2 || 'digits. Do not add 0 before the number.'}
      </div>
    </div>
  )
}

function Card({ children, style = {} }) {
  return <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '20px', ...style }}>{children}</div>
}

function SectionLabel({ children }) {
  return <div style={{ fontSize: '10.5px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '14px' }}>{children}</div>
}

function InfoBox({ children, color = 'var(--primary)', bg }) {
  return <div style={{ borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: '12px', background: bg || color + '10', border: '1px solid ' + color + '30', color, lineHeight: 1.6, marginBottom: '14px' }}>{children}</div>
}

function Toggle({ label, desc, value, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 0', borderBottom: '1px solid #F5F5F5' }}>
      <div style={{ flex: 1, minWidth: 0, marginRight: '16px' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{label}</div>
        {desc && <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '2px' }}>{desc}</div>}
      </div>
      <button type="button" onClick={() => onChange(!value)}
        style={{ width: '44px', height: '24px', borderRadius: 'var(--radius-lg)', background: value ? 'var(--primary)' : '#D1D5DB', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
        <div style={{ position: 'absolute', top: '2px', width: '20px', height: '20px', borderRadius: '50%', background: 'var(--bg-card)', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s', left: value ? '22px' : '2px' }} />
      </button>
    </div>
  )
}

function SelectField({ label, value, onChange, options, optional }) {
  const [focused, setFocused] = useState(false)
  return (
    <div>
      <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
        {label}{optional && <span style={{ fontWeight: 400, color: '#B0B3B8', marginLeft: '4px' }}>optional</span>}
      </label>
      <select value={value} onChange={e => onChange(e.target.value)} style={{ ...inputStyle(focused) }}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}>
        <option value="">Select...</option>
        {options.map(o => typeof o === 'string' ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  )
}

function SaveBtn({ onClick, saving, label }) {
  return (
    <button onClick={onClick} disabled={saving}
      style={{ width: '100%', padding: '12px', borderRadius: 'var(--radius-md)', background: saving ? 'var(--border)' : 'var(--primary)', border: 'none', fontSize: '13px', fontWeight: 700, color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', transition: 'background 0.15s' }}>
      {saving ? '...' : label}
    </button>
  )
}

function Settings() {
  const { seller, user, signOut, getPlanLabel, subscriptionDaysLeft, isSubscriptionExpired, getSubscriptionPlan } = useAuth()
  const navigate   = useNavigate()
  const location   = useLocation()
  const { showToast } = useToast()
  const { t }      = useTranslation()

  const [activeTab, setActiveTab]   = useState('shop')
  const [shopLogo, setShopLogo]     = useState(null)
  const [saving, setSaving]         = useState(false)
  const [savingPass, setSavingPass] = useState(false)
  const [sendingVerification, setSendingVerification] = useState(false)
  const [verificationSent, setVerificationSent]       = useState(false)
  const [showSignOutConfirm, setShowSignOutConfirm]   = useState(false)
  const [promoCode, setPromoCode]         = useState('')
  const [referrals, setReferrals]         = useState([])
  const [referralsLoading, setReferralsLoading] = useState(false)
  const [payoutWalletType, setPayoutWalletType]   = useState('')
  const [payoutWalletNumber, setPayoutWalletNumber] = useState('')
  const [copySuccess, setCopySuccess]   = useState(false)
  const [savingPayout, setSavingPayout] = useState(false)
  const [passwords, setPasswords]   = useState({ newPass: '', confirm: '' })
  const [passError, setPassError]   = useState('')
  const [mobilePayments, setMobilePayments] = useState([{ service: 'bKash', account: '' }])

  const [activityLogs, setActivityLogs]     = useState([])
  const [activityLoading, setActivityLoading] = useState(false)
  const [activityPage, setActivityPage]     = useState(1)
  const [activityTotal, setActivityTotal]   = useState(0)
  const [activityFilter, setActivityFilter] = useState('all')
  const [activityDateFrom, setActivityDateFrom] = useState('')
  const [activityDateTo, setActivityDateTo]     = useState('')
  const [activityDoneBy, setActivityDoneBy]     = useState('all')

  const [invoiceSettings, setInvoiceSettings] = useState({ invoice_prefix: 'INV', invoice_footer_note: '', show_logo_on_invoice: true, show_signature_field: false, tax_label: 'VAT', default_due_days: 7, invoice_terms: '' })
  const [printerSettings, setPrinterSettings] = useState({ printer_type: 'thermal_80mm', auto_print_on_sale: false, print_customer_copy: true, print_shop_copy: false, custom_footer: '' })
  const [alertSettings, setAlertSettings]     = useState({ low_stock_threshold: 5, notify_low_stock: true, notify_new_order: true, notify_pending_payment: true, notify_return: true, daily_summary: false })
  const [courierSettings, setCourierSettings] = useState({ pathao_client_id: '', pathao_client_secret: '', pathao_enabled: false, ecourier_api_key: '', ecourier_api_secret: '', ecourier_user_id: '', ecourier_enabled: false, redx_api_key: '', redx_enabled: false, steadfast_api_key: '', steadfast_secret_key: '', steadfast_enabled: false })

  const [form, setForm] = useState({
    shop_name: '', phone_country_code: '+880', phone: '', facebook_url: '', district: '', category: '',
    owner_name: '', owner_facebook_url: '', owner_address: '', owner_phone_country_code: '+880',
    owner_phone: '', owner_email: '', owner_nid: '', shop_address: '', country: '', seller_currency: 'BDT',
    trade_license: '', company_etin: '', bin_number: '', vat_reg_number: '',
    abn_number: '', acn_number: '', intl_reg_number: '', intl_reg_label: '',
    bank_name: '', bank_address: '', bank_city: '', bank_country: '', bank_account: '', bank_routing: '',
    bank_swift: '', bank_iban: '', bank_bsb: '', bank_sort_code: '',
    instagram_url: '', whatsapp_country_code: '+880', whatsapp_number: '',
    tiktok_url: '', website_url: '', youtube_url: '', nid_number: '',
  })

  const set   = (key, val) => setForm(f => ({ ...f, [key]: val }))
  const setIS = (key, val) => setInvoiceSettings(s => ({ ...s, [key]: val }))
  const setPS = (key, val) => setPrinterSettings(s => ({ ...s, [key]: val }))
  const setAS = (key, val) => setAlertSettings(s => ({ ...s, [key]: val }))
  const setCS = (key, val) => setCourierSettings(s => ({ ...s, [key]: val }))

  const plan      = getSubscriptionPlan()
  const planLabel = getPlanLabel()
  const daysLeft  = subscriptionDaysLeft()
  const expired   = isSubscriptionExpired()

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const tab = params.get('tab')
    if (tab) setActiveTab(tab)
  }, [location.search])

  useEffect(() => {
    if (seller) {
      setForm(f => ({ ...f,
        shop_name: seller.shop_name || '', phone_country_code: seller.phone_country_code || '+880',
        phone: seller.phone || '', facebook_url: seller.facebook_url || '',
        district: seller.district || '', category: seller.category || '',
        owner_name: seller.owner_name || '', owner_facebook_url: seller.owner_facebook_url || '',
        owner_address: seller.owner_address || '', owner_phone_country_code: seller.owner_phone_country_code || '+880',
        owner_phone: seller.owner_phone || '', owner_email: seller.owner_email || '',
        owner_nid: seller.owner_nid || seller.nid_number || '', shop_address: seller.shop_address || '',
        country: seller.country || '', seller_currency: seller.seller_currency || 'BDT',
        trade_license: seller.trade_license || '', company_etin: seller.company_etin || '',
        bin_number: seller.bin_number || '', vat_reg_number: seller.vat_reg_number || '',
        abn_number: seller.abn_number || '', acn_number: seller.acn_number || '',
        intl_reg_number: seller.intl_reg_number || '', intl_reg_label: seller.intl_reg_label || '',
        bank_name: seller.bank_name || '', bank_address: seller.bank_address || '',
        bank_city: seller.bank_city || '', bank_country: seller.bank_country || '',
        bank_account: seller.bank_account || '', bank_routing: seller.bank_routing || '',
        bank_swift: seller.bank_swift || '', bank_iban: seller.bank_iban || '',
        bank_bsb: seller.bank_bsb || '', bank_sort_code: seller.bank_sort_code || '',
        bank_account_name: seller.bank_account_name || '',
        instagram_url: seller.instagram_url || '', whatsapp_country_code: seller.whatsapp_country_code || '+880',
        whatsapp_number: seller.whatsapp_number || '', tiktok_url: seller.tiktok_url || '',
        website_url: seller.website_url || '', youtube_url: seller.youtube_url || '',
        nid_number: seller.nid_number || '',
      }))
      fetchShopLogo(); fetchMobilePayments(); fetchInvoiceSettings()
      fetchPrinterSettings(); fetchAlertSettings(); fetchCourierSettings(); fetchReferralData()
    }
  }, [seller]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'activity' && seller?.id) fetchActivityLogs()
  }, [activeTab, activityPage, activityFilter, activityDoneBy, activityDateFrom, activityDateTo]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchShopLogo       = async () => { const { data } = await supabase.from('seller_websites').select('logo_url').eq('seller_id', seller.id).single(); if (data?.logo_url) setShopLogo(data.logo_url) }
  const fetchMobilePayments = async () => { const { data } = await supabase.from('seller_mobile_payments').select('*').eq('seller_id', seller.id).order('created_at', { ascending: true }); if (data && data.length > 0) setMobilePayments(data.map(d => ({ id: d.id, service: d.service, account: d.account, region: d.region }))) }
  const fetchInvoiceSettings = async () => { const { data } = await supabase.from('seller_invoice_settings').select('*').eq('seller_id', seller.id).single(); if (data) setInvoiceSettings(s => ({ ...s, ...data })) }
  const fetchPrinterSettings = async () => { const { data } = await supabase.from('seller_printer_settings').select('*').eq('seller_id', seller.id).single(); if (data) setPrinterSettings(s => ({ ...s, ...data })) }
  const fetchAlertSettings   = async () => { const { data } = await supabase.from('seller_alert_settings').select('*').eq('seller_id', seller.id).single(); if (data) setAlertSettings(s => ({ ...s, ...data })) }
  const fetchCourierSettings = async () => { const { data } = await supabase.from('seller_courier_settings').select('*').eq('seller_id', seller.id).single(); if (data) setCourierSettings(s => ({ ...s, ...data })) }

  const fetchActivityLogs = async () => {
    setActivityLoading(true)
    let query = supabase.from('activity_logs').select('*', { count: 'exact' }).eq('seller_id', seller.id).order('created_at', { ascending: false }).range((activityPage - 1) * ITEMS_PER_PAGE, activityPage * ITEMS_PER_PAGE - 1)
    if (activityFilter !== 'all') query = query.eq('action', activityFilter)
    if (activityDoneBy !== 'all') query = query.eq('done_by', activityDoneBy)
    if (activityDateFrom) query = query.gte('created_at', activityDateFrom + 'T00:00:00')
    if (activityDateTo)   query = query.lte('created_at', activityDateTo + 'T23:59:59')
    const { data, count } = await query
    setActivityLogs(data || []); setActivityTotal(count || 0); setActivityLoading(false)
  }

  const fetchReferralData = async () => {
    if (!seller?.id) return
    const { data: sellerData } = await supabase.from('sellers').select('promo_code, payout_wallet_type, payout_wallet_number').eq('id', seller.id).single()
    if (sellerData) { setPromoCode(sellerData.promo_code || ''); setPayoutWalletType(sellerData.payout_wallet_type || ''); setPayoutWalletNumber(sellerData.payout_wallet_number || '') }
    setReferralsLoading(true)
    const { data: refData } = await supabase.from('referrals').select('*, referee:referee_seller_id(shop_name, seller_currency)').eq('referrer_seller_id', seller.id).order('created_at', { ascending: false })
    setReferrals(refData || []); setReferralsLoading(false)
  }

  const handleSavePayoutWallet = async () => {
    setSavingPayout(true)
    await supabase.from('sellers').update({ payout_wallet_type: payoutWalletType, payout_wallet_number: payoutWalletNumber }).eq('id', seller.id)
    setSavingPayout(false); showToast(t('settings.toast_payout'))
  }

  const handleCopyPromoCode = () => {
    if (!promoCode) return
    navigator.clipboard.writeText(promoCode).then(() => { setCopySuccess(true); setTimeout(() => setCopySuccess(false), 2000) })
  }

  const handleSaveShop = async () => {
    setSaving(true)
    await supabase.from('sellers').update({
      shop_name: form.shop_name, phone: form.phone, phone_country_code: form.phone_country_code,
      facebook_url: form.facebook_url, district: form.district, category: form.category,
      owner_name: form.owner_name, owner_facebook_url: form.owner_facebook_url, owner_address: form.owner_address,
      owner_phone: form.owner_phone, owner_phone_country_code: form.owner_phone_country_code,
      owner_email: form.owner_email, owner_nid: form.owner_nid, shop_address: form.shop_address,
      trade_license: form.trade_license, company_etin: form.company_etin, bin_number: form.bin_number, vat_reg_number: form.vat_reg_number,
      abn_number: form.abn_number, acn_number: form.acn_number, intl_reg_number: form.intl_reg_number, intl_reg_label: form.intl_reg_label,
      bank_name: form.bank_name, bank_address: form.bank_address, bank_city: form.bank_city, bank_country: form.bank_country,
      bank_account: form.bank_account, bank_routing: form.bank_routing, bank_swift: form.bank_swift, bank_iban: form.bank_iban,
      bank_bsb: form.bank_bsb, bank_sort_code: form.bank_sort_code, bank_account_name: form.bank_account_name || null,
      instagram_url: form.instagram_url, whatsapp_number: form.whatsapp_number, whatsapp_country_code: form.whatsapp_country_code,
      tiktok_url: form.tiktok_url, website_url: form.website_url, youtube_url: form.youtube_url,
    }).eq('id', seller?.id)
    await supabase.from('seller_mobile_payments').delete().eq('seller_id', seller.id)
    const valid = mobilePayments.filter(p => p.service && p.account)
    if (valid.length > 0) await supabase.from('seller_mobile_payments').insert(valid.map(p => ({ seller_id: seller.id, service: p.service, account: p.account, region: p.region })))
    setSaving(false); showToast(t('settings.toast_shop_saved'))
  }

  const handleSaveInvoice  = async () => { setSaving(true); await supabase.from('seller_invoice_settings').upsert({ seller_id: seller.id, ...invoiceSettings }, { onConflict: 'seller_id' }); setSaving(false); showToast(t('settings.toast_invoice_saved')) }
  const handleSavePrinter  = async () => { setSaving(true); await supabase.from('seller_printer_settings').upsert({ seller_id: seller.id, ...printerSettings }, { onConflict: 'seller_id' }); setSaving(false); showToast(t('settings.toast_printer_saved')) }
  const handleSaveAlerts   = async () => { setSaving(true); await supabase.from('seller_alert_settings').upsert({ seller_id: seller.id, ...alertSettings }, { onConflict: 'seller_id' }); setSaving(false); showToast(t('settings.toast_alerts_saved')) }
  const handleSaveCourier  = async () => { setSaving(true); await supabase.from('seller_courier_settings').upsert({ seller_id: seller.id, ...courierSettings }, { onConflict: 'seller_id' }); setSaving(false); showToast(t('settings.toast_courier_saved')) }

  const handleChangePassword = async () => {
    setPassError('')
    if (!passwords.newPass || passwords.newPass.length < 6) { setPassError(t('settings.pass_error_short')); return }
    if (passwords.newPass !== passwords.confirm) { setPassError(t('settings.pass_error_match')); return }
    setSavingPass(true)
    const { error } = await supabase.auth.updateUser({ password: passwords.newPass })
    if (error) setPassError(error.message)
    else { setPasswords({ newPass: '', confirm: '' }); showToast(t('settings.toast_password_changed')) }
    setSavingPass(false)
  }

  const handleRequestVerification = async () => {
    setSendingVerification(true)
    await supabase.from('sellers').update({ verification_status: 'pending', verification_requested_at: new Date().toISOString() }).eq('id', seller?.id)
    setSendingVerification(false); setVerificationSent(true)
    showToast(t('settings.toast_verification'))
  }

  const handleSignOut = async () => { await signOut(); navigate('/login') }

  const verificationReady = form.owner_name && form.owner_nid && form.owner_address
  const currencyFlag      = COUNTRY_FLAGS[form.country] || COUNTRY_FLAGS[seller?.country] || ''
  const activityTotalPages = Math.ceil(activityTotal / ITEMS_PER_PAGE)

  function timeAgo(date) {
    const s = Math.floor((new Date() - new Date(date)) / 1000)
    if (s < 60) return 'just now'
    if (s < 3600) return Math.floor(s / 60) + 'm ago'
    if (s < 86400) return Math.floor(s / 3600) + 'h ago'
    return Math.floor(s / 86400) + 'd ago'
  }

  const TABS = [
    { key: 'shop',     label: t('settings.tab_shop'),     iconPath: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { key: 'pos',      label: 'POS',                      iconPath: 'M9 7H6a2 2 0 00-2 2v9a2 2 0 002 2h9a2 2 0 002-2v-3M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 7v2a2 2 0 01-2 2h-2M7 21v-2a2 2 0 012-2h2' },
    { key: 'tara',     label: 'Tara',                     iconPath: 'M12 2C12 2 12.8 7.5 14.5 9.5C16.2 11.5 22 12 22 12C22 12 16.2 12.5 14.5 14.5C12.8 16.5 12 22 12 22C12 22 11.2 16.5 9.5 14.5C7.8 12.5 2 12 2 12C2 12 7.8 11.5 9.5 9.5C11.2 7.5 12 2 12 2Z' },
    { key: 'invoice',  label: t('settings.tab_invoice'),  iconPath: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { key: 'printer',  label: t('settings.tab_printer'),  iconPath: 'M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6z' },
    { key: 'alerts',   label: t('settings.tab_alerts'),   iconPath: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' },
    { key: 'couriers', label: t('settings.tab_couriers'), iconPath: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
    { key: 'referral', label: t('settings.tab_referral'), iconPath: 'M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7' },
    { key: 'activity', label: t('settings.tab_activity'), iconPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
    { key: 'account',  label: t('settings.tab_account'),  iconPath: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
  ]

  const phoneHint1 = t('settings.phone_digits_hint')
  const phoneHint2 = t('settings.phone_digits_hint2')
  const searchPh   = t('settings.phone_search_ph')
  const noResults  = t('settings.phone_no_results')

  return (
    <div style={{ flex:1, overflowX:'hidden', background:'var(--bg-page)', fontFamily:'var(--font-ui)' }}>
      <Topbar title={t('settings.title')} />
      <div style={{ padding: '18px 24px' }}>

        {/* Header */}
        <div className="pc-fade-up" style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '18px', padding: '16px', borderRadius: 'var(--radius-lg)', background: 'var(--bg-card)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
          {shopLogo ? (
            <img src={shopLogo} alt="logo" style={{ width: '52px', height: '52px', borderRadius: 'var(--radius-lg)', objectFit: 'cover', border: '1px solid var(--border)', flexShrink: 0 }} />
          ) : (
            <div style={{ width: '52px', height: '52px', borderRadius: 'var(--radius-lg)', background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: 700, color: 'var(--primary)', flexShrink: 0 }}>
              {seller?.shop_name?.slice(0, 1) || '?'}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '17px', fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{seller?.shop_name}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{user?.email}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '5px' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: 'var(--radius-full)', background: 'var(--primary)', color: '#fff' }}>{planLabel}</span>
              {seller?.verification_status === 'verified' && (
                <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--radius-full)', background: '#ECFDF5', color: '#065F46' }}>{t('settings.verified_badge')}</span>
              )}
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            {daysLeft !== null && plan !== 'suspended' && (
              <div style={{ fontSize: '12px', fontWeight: 600, color: expired ? '#DC2626' : daysLeft <= 7 ? '#D97706' : '#16A34A' }}>
                {expired ? t('settings.expired') : daysLeft + ' ' + t('settings.days_left')}
              </div>
            )}
            {!expired && plan !== 'suspended' && (
              <button onClick={() => navigate('/pricing')} style={{ fontSize: '12px', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', marginTop: '2px' }}>
                {plan === 'trial' ? t('settings.upgrade') : t('settings.manage')} {'→'}
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', padding: '4px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', background: 'var(--bg-card)', marginBottom: '18px', overflowX: 'auto', boxShadow: 'var(--shadow-sm)' }}>
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: 'var(--radius-md)', flexShrink: 0, background: activeTab === tab.key ? 'var(--primary)' : 'transparent', color: activeTab === tab.key ? '#fff' : 'var(--text-muted)', border: 'none', cursor: 'pointer', fontSize: '12.5px', fontWeight: 600, transition: 'all 0.15s', whiteSpace: 'nowrap' }}>
              <Icon path={tab.iconPath} color={activeTab === tab.key ? '#fff' : 'var(--text-muted)'} size={13} strokeWidth={2} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* ── POS TAB ── */}
        {activeTab === 'pos' && (
          <POSSettingsTab seller={seller} showToast={showToast} plan={plan} />
        )}

        {/* ── TARA TAB ── */}
        {activeTab === 'tara' && (
          <TaraSettingsTab seller={seller} showToast={showToast} />
        )}

        {/* ── SHOP TAB ── */}
        {activeTab === 'shop' && (
          <div className="pc-fade-up" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Card>
              <SectionLabel>{t('settings.shop_info')}</SectionLabel>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }} className="settings-grid">
                <div style={{ gridColumn: 'span 2' }}>
                  <FieldInput label={t('settings.shop_name') + ' *'} value={form.shop_name} onChange={v => set('shop_name', v)} placeholder={t('settings.shop_name_ph')} />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <PhoneField label={t('settings.shop_phone')} countryCode={form.phone_country_code} phone={form.phone} onCountryCodeChange={v => set('phone_country_code', v)} onPhoneChange={v => set('phone', v)} searchPh={searchPh} noResults={noResults} digitHint1={phoneHint1} digitHint2={phoneHint2} />
                </div>
                <SelectField label={t('settings.shop_category')} value={form.category} onChange={v => set('category', v)} options={CATEGORIES} />
                <FieldInput label={t('settings.shop_district')} value={form.district} onChange={v => set('district', v)} placeholder={t('settings.shop_district_ph')} optional />
                <div style={{ gridColumn: 'span 2' }}>
                  <FieldInput label={t('settings.shop_facebook')} value={form.facebook_url} onChange={v => set('facebook_url', v)} placeholder={t('settings.shop_facebook_ph')} optional />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <FieldTextarea label={t('settings.shop_address')} value={form.shop_address} onChange={v => set('shop_address', v)} placeholder={t('settings.shop_address_ph')} optional />
                </div>
              </div>
            </Card>

            <Card>
              <SectionLabel>{t('settings.social_title')}</SectionLabel>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }} className="settings-grid">
                <FieldInput label={t('settings.instagram')} value={form.instagram_url} onChange={v => set('instagram_url', v)} placeholder={t('settings.instagram_ph')} optional />
                <div style={{ gridColumn: 'span 2' }}>
                  <PhoneField label={t('settings.whatsapp')} optional countryCode={form.whatsapp_country_code} phone={form.whatsapp_number} onCountryCodeChange={v => set('whatsapp_country_code', v)} onPhoneChange={v => set('whatsapp_number', v)} searchPh={searchPh} noResults={noResults} digitHint1={phoneHint1} digitHint2={phoneHint2} />
                </div>
                <FieldInput label={t('settings.tiktok')} value={form.tiktok_url} onChange={v => set('tiktok_url', v)} placeholder={t('settings.tiktok_ph')} optional />
                <FieldInput label={t('settings.website')} value={form.website_url} onChange={v => set('website_url', v)} placeholder={t('settings.website_ph')} optional />
                <div style={{ gridColumn: 'span 2' }}>
                  <FieldInput label={t('settings.youtube')} value={form.youtube_url} onChange={v => set('youtube_url', v)} placeholder={t('settings.youtube_ph')} optional />
                </div>
              </div>
            </Card>

            <Card>
              <SectionLabel>{t('settings.owner_title')}</SectionLabel>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }} className="settings-grid">
                <div style={{ gridColumn: 'span 2' }}>
                  <FieldInput label={t('settings.owner_name')} value={form.owner_name} onChange={v => set('owner_name', v)} placeholder={t('settings.owner_name_ph')} />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <PhoneField label={t('settings.owner_phone')} countryCode={form.owner_phone_country_code} phone={form.owner_phone} onCountryCodeChange={v => set('owner_phone_country_code', v)} onPhoneChange={v => set('owner_phone', v)} searchPh={searchPh} noResults={noResults} digitHint1={phoneHint1} digitHint2={phoneHint2} />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <FieldInput label={t('settings.owner_email')} value={form.owner_email} onChange={v => set('owner_email', v)} placeholder={t('settings.owner_email_ph')} type="email" optional />
                </div>
                <FieldInput label={t('settings.owner_nid')} value={form.owner_nid} onChange={v => set('owner_nid', v)} placeholder={t('settings.owner_nid_ph')} />
                <div style={{ gridColumn: 'span 2' }}>
                  <FieldTextarea label={t('settings.owner_address')} value={form.owner_address} onChange={v => set('owner_address', v)} placeholder={t('settings.owner_address_ph')} optional />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <FieldInput label={t('settings.owner_facebook')} value={form.owner_facebook_url} onChange={v => set('owner_facebook_url', v)} placeholder={t('settings.owner_facebook_ph')} optional />
                </div>
              </div>
            </Card>

            <Card>
              <SectionLabel>{t('settings.biz_title')}</SectionLabel>
              <InfoBox color="#D97706" bg="#FFFBEB"><strong>Why we need this:</strong> {t('settings.biz_why')}</InfoBox>
              <InfoBox color={'var(--text-muted)'} bg={'var(--bg-muted)'}>{t('settings.biz_disclaimer')}</InfoBox>
              <SectionLabel>{t('settings.biz_bd')}</SectionLabel>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }} className="settings-grid">
                <FieldInput label={t('settings.trade_license')} value={form.trade_license} onChange={v => set('trade_license', v)} placeholder={t('settings.trade_license_ph')} optional />
                <FieldInput label={t('settings.company_etin')} value={form.company_etin} onChange={v => set('company_etin', v)} placeholder={t('settings.company_etin_ph')} optional />
                <FieldInput label={t('settings.bin_number')} value={form.bin_number} onChange={v => set('bin_number', v)} placeholder={t('settings.bin_number_ph')} optional />
                <FieldInput label={t('settings.vat_reg')} value={form.vat_reg_number} onChange={v => set('vat_reg_number', v)} placeholder={t('settings.vat_reg_ph')} optional />
              </div>
              <SectionLabel>{t('settings.biz_intl')}</SectionLabel>
              <InfoBox color={'var(--text-muted)'} bg={'var(--bg-muted)'}>{t('settings.biz_intl_info')}</InfoBox>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }} className="settings-grid">
                <FieldInput label={t('settings.abn')} value={form.abn_number} onChange={v => set('abn_number', v)} placeholder={t('settings.abn_ph')} optional />
                <FieldInput label={t('settings.acn')} value={form.acn_number} onChange={v => set('acn_number', v)} placeholder={t('settings.acn_ph')} optional />
                <FieldInput label={t('settings.intl_reg')} value={form.intl_reg_number} onChange={v => set('intl_reg_number', v)} placeholder={t('settings.intl_reg_ph')} optional />
                <FieldInput label={t('settings.intl_reg_label')} value={form.intl_reg_label} onChange={v => set('intl_reg_label', v)} placeholder={t('settings.intl_reg_label_ph')} optional />
              </div>
            </Card>

            <Card>
              <SectionLabel>{t('settings.payment_title')}</SectionLabel>
              <InfoBox color={'var(--text-muted)'} bg={'var(--bg-muted)'}>{t('settings.payment_info')}</InfoBox>
              <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '8px' }}>{t('settings.bd_payments')}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', marginBottom: '8px' }}>
                {mobilePayments.filter(p => p.region === 'bd' || !p.region).map((payment, i) => {
                  const realIdx = mobilePayments.indexOf(payment)
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <select value={payment.service} onChange={e => setMobilePayments(p => p.map((item, idx) => idx === realIdx ? { ...item, service: e.target.value } : item))} style={{ ...inputStyle(false), width: '110px', flexShrink: 0 }}>
                        {['bKash','Nagad','Rocket','Upay','Other'].map(s => <option key={s}>{s}</option>)}
                      </select>
                      <input type="text" value={payment.account} onChange={e => setMobilePayments(p => p.map((item, idx) => idx === realIdx ? { ...item, account: e.target.value } : item))} placeholder={t('settings.account_ph')} style={{ ...inputStyle(false), flex: 1 }} />
                      <button onClick={() => setMobilePayments(p => p.filter((_, idx) => idx !== realIdx))} style={{ width: '30px', height: '30px', borderRadius: 'var(--radius-md)', background: '#FEF2F2', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon path={IC.x} color="#DC2626" size={13} strokeWidth={2.5} />
                      </button>
                    </div>
                  )
                })}
              </div>
              <button onClick={() => setMobilePayments(p => [...p, { service: 'bKash', account: '', region: 'bd' }])} style={{ width: '100%', padding: '8px', borderRadius: 'var(--radius-md)', border: '2px dashed var(--border)', background: 'var(--bg-card)', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer', marginBottom: '16px' }}>
                {t('settings.add_bd_payment')}
              </button>
              <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '8px' }}>{t('settings.intl_payments')}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', marginBottom: '8px' }}>
                {mobilePayments.filter(p => p.region === 'intl').map((payment, i) => {
                  const realIdx = mobilePayments.indexOf(payment)
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <select value={payment.service} onChange={e => setMobilePayments(p => p.map((item, idx) => idx === realIdx ? { ...item, service: e.target.value } : item))} style={{ ...inputStyle(false), width: '110px', flexShrink: 0 }}>
                        {['PayID','Zelle','PayPal','Wise','Revolut','Cash App','Venmo','PayNow','WeChat Pay','Alipay','Google Pay','Apple Pay','Other'].map(s => <option key={s}>{s}</option>)}
                      </select>
                      <input type="text" value={payment.account} onChange={e => setMobilePayments(p => p.map((item, idx) => idx === realIdx ? { ...item, account: e.target.value } : item))} placeholder={t('settings.intl_account_ph')} style={{ ...inputStyle(false), flex: 1 }} />
                      <button onClick={() => setMobilePayments(p => p.filter((_, idx) => idx !== realIdx))} style={{ width: '30px', height: '30px', borderRadius: 'var(--radius-md)', background: '#FEF2F2', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon path={IC.x} color="#DC2626" size={13} strokeWidth={2.5} />
                      </button>
                    </div>
                  )
                })}
              </div>
              <button onClick={() => setMobilePayments(p => [...p, { service: 'PayID', account: '', region: 'intl' }])} style={{ width: '100%', padding: '8px', borderRadius: 'var(--radius-md)', border: '2px dashed var(--border)', background: 'var(--bg-card)', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}>
                {t('settings.add_intl_payment')}
              </button>
            </Card>

            <Card>
              <SectionLabel>{t('settings.bank_title')}</SectionLabel>
              <InfoBox color={'var(--text-muted)'} bg={'var(--bg-muted)'}>{t('settings.bank_info')}</InfoBox>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }} className="settings-grid">
                <div style={{ gridColumn: 'span 2' }}><FieldInput label={t('settings.bank_name')} value={form.bank_name} onChange={v => set('bank_name', v)} placeholder={t('settings.bank_name_ph')} optional /></div>
                <div style={{ gridColumn: 'span 2' }}><FieldInput label={t('settings.bank_account_name')} value={form.bank_account_name || ''} onChange={v => set('bank_account_name', v)} placeholder={t('settings.bank_account_name_ph')} optional /></div>
                {[
                  { key: 'bank_account', lk: 'bank_account' }, { key: 'bank_routing', lk: 'bank_routing' },
                  { key: 'bank_swift', lk: 'bank_swift' },     { key: 'bank_iban', lk: 'bank_iban' },
                  { key: 'bank_bsb', lk: 'bank_bsb' },         { key: 'bank_sort_code', lk: 'bank_sort' },
                  { key: 'bank_city', lk: 'bank_city' },       { key: 'bank_country', lk: 'bank_country' },
                ].map(f => <FieldInput key={f.key} label={t('settings.' + f.lk)} value={form[f.key]} onChange={v => set(f.key, v)} placeholder="" optional />)}
              </div>
            </Card>

            <Card>
              <SectionLabel>{t('settings.verify_title')}</SectionLabel>
              <InfoBox color="#D97706" bg="#FFFBEB">{t('settings.verify_info')}</InfoBox>
              <div style={{ marginTop: '8px' }}>
                {verificationSent || seller?.verification_status === 'pending' ? (
                  <InfoBox color="#16A34A" bg="#F0FDF4">{t('settings.verify_pending')}</InfoBox>
                ) : seller?.verification_status === 'verified' ? (
                  <InfoBox color="#16A34A" bg="#F0FDF4">{t('settings.verify_done')}</InfoBox>
                ) : (
                  <>
                    {!verificationReady && <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '10px' }}>{t('settings.verify_fill_first')}</div>}
                    <button onClick={handleRequestVerification} disabled={!verificationReady || sendingVerification}
                      style={{ width: '100%', padding: '11px', borderRadius: 'var(--radius-md)', background: verificationReady ? '#D97706' : 'var(--border)', border: 'none', fontSize: '13px', fontWeight: 700, color: '#fff', cursor: verificationReady ? 'pointer' : 'not-allowed' }}>
                      {sendingVerification ? t('settings.verify_submitting') : t('settings.verify_submit')}
                    </button>
                  </>
                )}
              </div>
            </Card>

            <Card>
              <SectionLabel>{t('settings.currency_title')}</SectionLabel>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>
                    {currencyFlag} {seller?.seller_currency || 'BDT'}{(form.country || seller?.country) ? ' · ' + (form.country || seller?.country) : ''}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.6 }}>{t('settings.currency_locked')} <a href="mailto:help@pageconnect.io" style={{ color: 'var(--primary)' }}>help@pageconnect.io</a></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, padding: '5px 10px', borderRadius: 'var(--radius-md)', background: '#FFF8E6', border: '1px solid #FDE68A' }}>
                  <Icon path={IC.lock} color="#D97706" size={13} strokeWidth={2} />
                  <span style={{ fontSize: '11.5px', fontWeight: 700, color: '#D97706' }}>{t('settings.currency_locked_badge')}</span>
                </div>
              </div>
            </Card>

            <SaveBtn onClick={handleSaveShop} saving={saving} label={t('settings.save_shop')} />
          </div>
        )}

        {/* ── INVOICE TAB ── */}
        {activeTab === 'invoice' && (
          <div className="pc-fade-up" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Card>
              <SectionLabel>{t('settings.invoice_config')}</SectionLabel>
              <InfoBox color={'var(--primary)'}>{t('settings.invoice_info')}</InfoBox>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }} className="settings-grid">
                <FieldInput label={t('settings.inv_prefix')} value={invoiceSettings.invoice_prefix} onChange={v => setIS('invoice_prefix', v)} placeholder="INV" />
                <FieldInput label={t('settings.inv_due_days')} value={String(invoiceSettings.default_due_days)} onChange={v => setIS('default_due_days', Number(v))} placeholder="7" type="number" />
                <FieldInput label={t('settings.inv_tax_label')} value={invoiceSettings.tax_label} onChange={v => setIS('tax_label', v)} placeholder="VAT / GST / Tax" />
                <div>
                  <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>{t('settings.inv_show_logo')}</label>
                  <button onClick={() => setIS('show_logo_on_invoice', !invoiceSettings.show_logo_on_invoice)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-md)', border: '1px solid ' + (invoiceSettings.show_logo_on_invoice ? 'var(--primary)' : 'var(--border)'), background: invoiceSettings.show_logo_on_invoice ? 'var(--primary-light)' : 'var(--bg-card)', fontSize: '12.5px', fontWeight: 600, color: invoiceSettings.show_logo_on_invoice ? 'var(--primary)' : 'var(--text-muted)', cursor: 'pointer' }}>
                    {invoiceSettings.show_logo_on_invoice ? t('settings.inv_show_logo_on') : t('settings.inv_show_logo_off')}
                  </button>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <FieldTextarea label={t('settings.inv_footer')} value={invoiceSettings.invoice_footer_note} onChange={v => setIS('invoice_footer_note', v)} placeholder={t('settings.inv_footer_ph')} optional />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <FieldTextarea label={t('settings.inv_terms')} value={invoiceSettings.invoice_terms} onChange={v => setIS('invoice_terms', v)} placeholder={t('settings.inv_terms_ph')} optional />
                </div>
              </div>
              <div style={{ borderTop: '1px solid var(--border)', marginTop: '14px', paddingTop: '14px' }}>
                <Toggle label={t('settings.inv_signature')} desc={t('settings.inv_signature_desc')} value={invoiceSettings.show_signature_field} onChange={v => setIS('show_signature_field', v)} />
              </div>
            </Card>
            <SaveBtn onClick={handleSaveInvoice} saving={saving} label={t('settings.save_invoice')} />
          </div>
        )}

        {/* ── PRINTER TAB ── */}
        {activeTab === 'printer' && (
          <div className="pc-fade-up" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Card>
              <SectionLabel>{t('settings.printer_config')}</SectionLabel>
              <InfoBox color={'var(--primary)'}>{t('settings.printer_info')}</InfoBox>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '8px' }}>{t('settings.paper_size')}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {[
                    { key: 'thermal_58mm', label: t('settings.thermal_58'), desc: t('settings.thermal_58_desc') },
                    { key: 'thermal_80mm', label: t('settings.thermal_80'), desc: t('settings.thermal_80_desc') },
                    { key: 'a4',           label: t('settings.a4'),         desc: t('settings.a4_desc') },
                  ].map(opt => (
                    <button key={opt.key} type="button" onClick={() => setPS('printer_type', opt.key)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: 'var(--radius-md)', border: '1px solid ' + (printerSettings.printer_type === opt.key ? 'var(--primary)' : 'var(--border)'), background: printerSettings.printer_type === opt.key ? 'var(--primary-light)' : 'var(--bg-card)', cursor: 'pointer', textAlign: 'left' }}>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{opt.label}</div>
                        <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '2px' }}>{opt.desc}</div>
                      </div>
                      {printerSettings.printer_type === opt.key && <Icon path={IC.check} color={'var(--primary)'} size={16} strokeWidth={2.5} />}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: '14px' }}>
                <Toggle label={t('settings.auto_print')} desc={t('settings.auto_print_desc')} value={printerSettings.auto_print_on_sale} onChange={v => setPS('auto_print_on_sale', v)} />
                <Toggle label={t('settings.customer_copy')} desc={t('settings.customer_copy_desc')} value={printerSettings.print_customer_copy} onChange={v => setPS('print_customer_copy', v)} />
                <Toggle label={t('settings.shop_copy')} desc={t('settings.shop_copy_desc')} value={printerSettings.print_shop_copy} onChange={v => setPS('print_shop_copy', v)} />
              </div>
              <div style={{ marginTop: '14px' }}>
                <FieldTextarea label={t('settings.custom_footer')} value={printerSettings.custom_footer} onChange={v => setPS('custom_footer', v)} placeholder={t('settings.custom_footer_ph')} optional />
              </div>
            </Card>
            <Card>
              <SectionLabel>{t('settings.test_print_title')}</SectionLabel>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>{t('settings.test_print_desc')}</div>
              <button onClick={() => window.print()} style={{ width: '100%', padding: '11px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer' }}>
                {t('settings.test_print_btn')}
              </button>
            </Card>
            <SaveBtn onClick={handleSavePrinter} saving={saving} label={t('settings.save_printer')} />
          </div>
        )}

        {/* ── ALERTS TAB ── */}
        {activeTab === 'alerts' && (
          <div className="pc-fade-up" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Card>
              <SectionLabel>{t('settings.alerts_title')}</SectionLabel>
              <Toggle label={t('settings.alert_new_order')} desc={t('settings.alert_new_order_desc')} value={alertSettings.notify_new_order} onChange={v => setAS('notify_new_order', v)} />
              <Toggle label={t('settings.alert_low_stock')} desc={t('settings.alert_low_stock_desc')} value={alertSettings.notify_low_stock} onChange={v => setAS('notify_low_stock', v)} />
              <Toggle label={t('settings.alert_pending')} desc={t('settings.alert_pending_desc')} value={alertSettings.notify_pending_payment} onChange={v => setAS('notify_pending_payment', v)} />
              <Toggle label={t('settings.alert_return')} desc={t('settings.alert_return_desc')} value={alertSettings.notify_return} onChange={v => setAS('notify_return', v)} />
              <Toggle label={t('settings.alert_daily')} desc={t('settings.alert_daily_desc')} value={alertSettings.daily_summary} onChange={v => setAS('daily_summary', v)} />
            </Card>
            <Card>
              <SectionLabel>{t('settings.alert_threshold_title')}</SectionLabel>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>{t('settings.alert_threshold_desc')}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input type="number" value={alertSettings.low_stock_threshold} onChange={e => setAS('low_stock_threshold', Number(e.target.value))} min={1} max={50}
                  style={{ width: '80px', ...inputStyle(false), textAlign: 'center', fontSize: '16px', fontWeight: 700 }} />
                <span style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>{t('settings.alert_units')}</span>
              </div>
            </Card>
            <SaveBtn onClick={handleSaveAlerts} saving={saving} label={t('settings.save_alerts')} />
          </div>
        )}

        {/* ── COURIERS TAB ── */}
        {activeTab === 'couriers' && (
          <div className="pc-fade-up" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <InfoBox color={'var(--primary)'}>{t('settings.courier_info')}</InfoBox>
            {[
              { key: 'pathao', label: 'Pathao Courier', fields: [{ key: 'pathao_client_id', label: 'Client ID', placeholder: 'Pathao merchant client ID' }, { key: 'pathao_client_secret', label: 'Client secret', placeholder: 'Pathao client secret', type: 'password' }], link: 'https://merchant.pathao.com', linkLabel: 'Get credentials from Pathao Merchant Portal' },
              { key: 'ecourier', label: 'eCourier', fields: [{ key: 'ecourier_api_key', label: 'API key', placeholder: 'eCourier API key' }, { key: 'ecourier_api_secret', label: 'API secret', placeholder: 'eCourier API secret', type: 'password' }, { key: 'ecourier_user_id', label: 'User ID', placeholder: 'eCourier merchant user ID' }], link: 'https://portal.ecourier.com.bd', linkLabel: 'Get credentials from eCourier Portal' },
              { key: 'redx', label: 'RedX', fields: [{ key: 'redx_api_key', label: 'API key', placeholder: 'RedX API key', type: 'password' }], link: 'https://redx.com.bd/merchant', linkLabel: 'Get credentials from RedX Merchant Portal' },
              { key: 'steadfast', label: 'Steadfast Courier', fields: [{ key: 'steadfast_api_key', label: 'API key', placeholder: 'Steadfast API key' }, { key: 'steadfast_secret_key', label: 'Secret key', placeholder: 'Steadfast secret key', type: 'password' }], link: 'https://steadfast.com.bd', linkLabel: 'Get credentials from Steadfast Portal' },
            ].map(courier => (
              <Card key={courier.key} style={{ transition: 'box-shadow 0.15s' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: 'var(--radius-md)', background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon path={IC.api} color={'var(--primary)'} size={18} strokeWidth={1.8} />
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>{courier.label}</div>
                      <a href={courier.link} target="_blank" rel="noreferrer" style={{ fontSize: '11.5px', color: 'var(--primary)' }}>{courier.linkLabel}</a>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '11.5px', color: courierSettings[courier.key + '_enabled'] ? '#16A34A' : 'var(--text-muted)', fontWeight: 600 }}>
                      {courierSettings[courier.key + '_enabled'] ? t('settings.courier_active') : t('settings.courier_inactive')}
                    </span>
                    <Toggle label="" value={courierSettings[courier.key + '_enabled']} onChange={v => setCS(courier.key + '_enabled', v)} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }} className="settings-grid">
                  {courier.fields.map(f => <FieldInput key={f.key} label={f.label} value={courierSettings[f.key] || ''} onChange={v => setCS(f.key, v)} placeholder={f.placeholder} type={f.type || 'text'} optional />)}
                </div>
              </Card>
            ))}
            <Card>
              <SectionLabel>{t('settings.courier_more')}</SectionLabel>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.7 }}>{t('settings.courier_more_desc')}</div>
            </Card>
            <SaveBtn onClick={handleSaveCourier} saving={saving} label={t('settings.save_couriers')} />
          </div>
        )}

        {/* ── ACTIVITY TAB ── */}
        {activeTab === 'activity' && (
          <div className="pc-fade-up" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Card>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: 1, minWidth: '160px' }}>
                  <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>{t('settings.activity_action')}</label>
                  <select value={activityFilter} onChange={e => { setActivityFilter(e.target.value); setActivityPage(1) }} style={inputStyle(false)}>
                    <option value="all">{t('settings.activity_all')}</option>
                    {Object.entries(ACTIVITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: '130px' }}>
                  <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>{t('settings.activity_done_by')}</label>
                  <select value={activityDoneBy} onChange={e => { setActivityDoneBy(e.target.value); setActivityPage(1) }} style={inputStyle(false)}>
                    <option value="all">{t('settings.activity_all_users')}</option>
                    <option value="Owner">Owner</option><option value="System">System</option>
                    <option value="Admin">Admin</option><option value="Content Admin">Content Admin</option>
                    <option value="Finance Admin">Finance Admin</option>
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: '140px' }}>
                  <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>{t('settings.activity_from')}</label>
                  <input type="date" value={activityDateFrom} onChange={e => { setActivityDateFrom(e.target.value); setActivityPage(1) }} style={inputStyle(false)} />
                </div>
                <div style={{ flex: 1, minWidth: '140px' }}>
                  <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>{t('settings.activity_to')}</label>
                  <input type="date" value={activityDateTo} onChange={e => { setActivityDateTo(e.target.value); setActivityPage(1) }} style={inputStyle(false)} />
                </div>
                {(activityFilter !== 'all' || activityDateFrom || activityDateTo || activityDoneBy !== 'all') && (
                  <button onClick={() => { setActivityFilter('all'); setActivityDateFrom(''); setActivityDateTo(''); setActivityPage(1) }}
                    style={{ padding: '9px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '12.5px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {t('settings.activity_clear')}
                  </button>
                )}
              </div>
            </Card>

            <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', overflow: 'hidden' }}>
              <div style={{ padding: '12px 18px', background: 'var(--bg-muted)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{t('settings.activity_title')}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  {activityTotal} {activityFilter !== 'all' ? t('settings.activity_filtered') + ' ' : t('settings.activity_total') + ' '}{t('settings.activity_entries')}
                </div>
              </div>

              {activityLoading ? (
                <div style={{ padding: '48px', textAlign: 'center' }}>
                  <div style={{ width: '22px', height: '22px', borderRadius: '50%', border: '2.5px solid var(--primary)', borderTopColor: 'transparent', animation: 'spin 0.7s linear infinite', margin: '0 auto 10px' }} />
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{t('settings.activity_loading')}</div>
                  <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                </div>
              ) : activityLogs.length === 0 ? (
                <div style={{ padding: '48px', textAlign: 'center' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', marginTop: '10px' }}>{t('settings.activity_no_logs')}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '3px' }}>{t('settings.activity_no_logs_desc')}</div>
                </div>
              ) : (
                <>
                  {activityLogs.map((log, i) => {
                    const color = ACTIVITY_COLORS[log.action] || 'var(--text-muted)'
                    const label = ACTIVITY_LABELS[log.action] || log.action
                    return (
                      <div key={log.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 18px', borderBottom: i < activityLogs.length - 1 ? '1px solid #F5F5F5' : 'none' }}>
                        <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', width: '28px', flexShrink: 0, paddingTop: '1px', textAlign: 'right' }}>{(activityPage - 1) * ITEMS_PER_PAGE + i + 1}</div>
                        <div style={{ width: '32px', height: '32px', borderRadius: 'var(--radius-md)', background: color + '12', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{label}</span>
                            <span style={{ fontSize: '11px', fontWeight: 600, padding: '1px 7px', borderRadius: 'var(--radius-full)', background: color + '12', color }}>{log.entity_type}</span>
                          </div>
                          {log.old_value && log.new_value && (
                            <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '3px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <span style={{ background: '#FEF2F2', color: '#DC2626', padding: '1px 6px', borderRadius: 'var(--radius-sm)', fontSize: '11px', fontFamily: 'JetBrains Mono, monospace' }}>{String(log.old_value).slice(0, 30)}</span>
                              <span style={{ color: 'var(--text-muted)' }}>{'→'}</span>
                              <span style={{ background: '#F0FDF4', color: '#16A34A', padding: '1px 6px', borderRadius: 'var(--radius-sm)', fontSize: '11px', fontFamily: 'JetBrains Mono, monospace' }}>{String(log.new_value).slice(0, 30)}</span>
                            </div>
                          )}
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '3px' }}>
                            {log.done_by || 'Owner'} · {new Date(log.created_at).toLocaleString('en-BD', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            <span style={{ marginLeft: '6px', color: '#B0B3B8' }}>{timeAgo(log.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    )
                  })}

                  {activityTotalPages > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderTop: '1px solid var(--border)', background: 'var(--bg-muted)', flexWrap: 'wrap', gap: '8px' }}>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        {t('settings.activity_showing')} <strong style={{ color: 'var(--text-primary)' }}>{(activityPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(activityPage * ITEMS_PER_PAGE, activityTotal)}</strong> {t('settings.activity_of')} <strong style={{ color: 'var(--text-primary)' }}>{activityTotal}</strong> {t('settings.activity_entries')}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <button onClick={() => setActivityPage(p => Math.max(1, p - 1))} disabled={activityPage === 1}
                          style={{ width: '30px', height: '30px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: activityPage === 1 ? 'not-allowed' : 'pointer', opacity: activityPage === 1 ? 0.4 : 1 }}>
                          <Icon path={IC.chevL} color={'var(--text-primary)'} size={13} strokeWidth={2} />
                        </button>
                        {Array.from({ length: Math.min(activityTotalPages, 5) }, (_, i) => i + 1).map(p => (
                          <button key={p} onClick={() => setActivityPage(p)}
                            style={{ width: '30px', height: '30px', borderRadius: 'var(--radius-md)', border: '1px solid ' + (activityPage === p ? 'var(--primary)' : 'var(--border)'), background: activityPage === p ? 'var(--primary)' : 'var(--bg-card)', color: activityPage === p ? 'var(--bg-card)' : 'var(--text-primary)', fontSize: '12px', fontWeight: activityPage === p ? 700 : 400, cursor: 'pointer' }}>
                            {p}
                          </button>
                        ))}
                        <button onClick={() => setActivityPage(p => Math.min(activityTotalPages, p + 1))} disabled={activityPage === activityTotalPages}
                          style={{ width: '30px', height: '30px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: activityPage === activityTotalPages ? 'not-allowed' : 'pointer', opacity: activityPage === activityTotalPages ? 0.4 : 1 }}>
                          <Icon path={IC.chevR} color={'var(--text-primary)'} size={13} strokeWidth={2} />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* ── REFERRAL TAB ── */}
        {activeTab === 'referral' && (() => {
          const isBD   = (seller?.seller_currency || 'BDT') === 'BDT'
          const symRef = isBD ? '৳' : '$'
          const tiers  = isBD ? [{ plan: 'Monthly (1 month)', reward: '৳50' }, { plan: 'Yearly (12 months)', reward: '৳500' }] : [{ plan: 'Monthly (1 month)', reward: '$10' }, { plan: 'Yearly (12 months)', reward: '$25' }]
          const totalEarned   = referrals.filter(r => r.status === 'paid').reduce((s, r) => s + Number(r.discount_amount || 0), 0)
          const pending       = referrals.filter(r => r.status === 'pending' || r.status === 'approved').length
          const bdWallets     = ['bKash', 'Nagad', 'Rocket', 'Upay', 'Bank transfer']
          const intlWallets   = ['PayPal', 'Wise', 'Bank transfer', 'Revolut', 'Other']
          const walletOptions = isBD ? bdWallets : intlWallets

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ background: 'linear-gradient(135deg, var(--primary) 0%, #0C5BCC 100%)', borderRadius: 'var(--radius-lg)', padding: '24px', color: '#fff' }}>
                <div style={{ fontSize: '20px', fontWeight: 800, marginBottom: '4px' }}>{t('settings.referral_title')}</div>
                <div style={{ fontSize: '13px', opacity: 0.8, marginBottom: '20px', lineHeight: 1.6 }}>{t('settings.referral_desc')}</div>
                <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: 'var(--radius-lg)', padding: '16px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>{t('settings.referral_code_label')}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ flex: 1, fontFamily: 'JetBrains Mono, monospace', fontSize: '20px', fontWeight: 800, letterSpacing: '0.05em' }}>{promoCode || t('settings.referral_loading')}</div>
                    <button onClick={handleCopyPromoCode} style={{ padding: '8px 16px', borderRadius: 'var(--radius-md)', background: copySuccess ? '#16A34A' : 'white', border: 'none', fontSize: '12px', fontWeight: 700, color: copySuccess ? 'white' : '#1877F2', cursor: 'pointer', flexShrink: 0, transition: 'all 0.2s', fontFamily: 'var(--font-ui)' }}>
                      {copySuccess ? t('settings.referral_copied') : t('settings.referral_copy')}
                    </button>
                  </div>
                  <div style={{ fontSize: '11px', opacity: 0.6, marginTop: '8px' }}>{t('settings.referral_share_hint')}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginTop: '14px' }}>
                  {[
                    { label: t('settings.referral_total'), value: referrals.length },
                    { label: t('settings.referral_pending'), value: pending },
                    { label: t('settings.referral_earned'), value: symRef + totalEarned.toLocaleString() },
                  ].map(s => (
                    <div key={s.label} style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 'var(--radius-md)', padding: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: 800 }}>{s.value}</div>
                      <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '2px' }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <Card>
                <SectionLabel>{t('settings.reward_tiers')}</SectionLabel>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                  {tiers.map(tier => (
                    <div key={tier.plan} style={{ background: 'var(--bg-muted)', borderRadius: 'var(--radius-lg)', padding: '16px', textAlign: 'center', border: '1px solid var(--border)' }}>
                      <div style={{ fontSize: '24px', fontWeight: 800, color: 'var(--primary)' }}>{tier.reward}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>{tier.plan}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>{t('settings.reward_both')}</div>
                    </div>
                  ))}
                </div>
                <InfoBox color={'var(--primary)'}>{t('settings.reward_info')}</InfoBox>
              </Card>

              <Card>
                <SectionLabel>{t('settings.payout_title')}</SectionLabel>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '14px', lineHeight: 1.6 }}>{t('settings.payout_desc')}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }} className="settings-grid">
                  <div>
                    <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>{t('settings.payout_type')}</label>
                    <select value={payoutWalletType} onChange={e => setPayoutWalletType(e.target.value)} style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '9px 12px', fontSize: '12.5px', outline: 'none', fontFamily: 'var(--font-ui)', background: 'var(--bg-card)', color: 'var(--text-primary)' }}>
                      <option value="">Select...</option>
                      {walletOptions.map(w => <option key={w}>{w}</option>)}
                    </select>
                  </div>
                  <FieldInput label={t('settings.payout_account')} value={payoutWalletNumber} onChange={v => setPayoutWalletNumber(v)} placeholder={isBD ? 'e.g. 01712345678' : 'e.g. email@paypal.com'} />
                </div>
                <div style={{ marginTop: '12px' }}>
                  <SaveBtn onClick={handleSavePayoutWallet} saving={savingPayout} label={t('settings.save_payout')} />
                </div>
              </Card>

              <Card>
                <SectionLabel>{t('settings.history_title')}</SectionLabel>
                {referralsLoading ? (
                  <div style={{ padding: '24px', textAlign: 'center', fontSize: '12px', color: 'var(--text-muted)' }}>Loading...</div>
                ) : referrals.length === 0 ? (
                  <div style={{ padding: '24px', textAlign: 'center' }}>
                    <div style={{ fontSize: '32px', marginBottom: '10px' }}>🔗</div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>{t('settings.history_empty')}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{t('settings.history_empty_desc')}</div>
                  </div>
                ) : referrals.map((ref, i) => {
                  const statusColors = { pending: { bg: '#FFF8E6', color: '#D97706' }, approved: { bg: '#EFF6FF', color: '#1877F2' }, paid: { bg: '#F0FDF4', color: '#16A34A' }, rejected: { bg: '#FEF2F2', color: '#DC2626' } }
                  const sc = statusColors[ref.status] || statusColors.pending
                  return (
                    <div key={ref.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0', borderBottom: i < referrals.length - 1 ? '1px solid #F5F5F5' : 'none' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: 'var(--radius-md)', background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>🏪</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{ref.referee?.shop_name || 'New seller'}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{ref.plan_type || 'Pending plan'} · {new Date(ref.created_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        {ref.discount_amount && <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--primary)' }}>{symRef}{ref.discount_amount}</div>}
                        <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--radius-full)', background: sc.bg, color: sc.color }}>
                          {ref.status.charAt(0).toUpperCase() + ref.status.slice(1)}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </Card>

              <Card>
                <SectionLabel>{t('settings.ref_tc')}</SectionLabel>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.8 }}>
                  {[
                    'Referral rewards are one-time only — not recurring per subscription renewal.',
                    'Rewards are triggered only when the referred seller makes their first successful subscription payment.',
                    'Free trial periods do not trigger referral rewards.',
                    'Both the referrer and referee must have active accounts in good standing at time of disbursement.',
                    'Reward amounts: Bangladesh — ৳50 (monthly), ৳500 (yearly). International — $10 (monthly), $25 (yearly).',
                    'Payments are disbursed to the wallet on file within 7 business days of qualification.',
                    'PageConnect reserves the right to withhold rewards in cases of suspected fraud, abuse, or violation of terms.',
                    'PageConnect reserves the right to modify or discontinue the referral program at any time with notice.',
                    'Self-referrals or referrals using fake accounts will be disqualified and may result in account suspension.',
                    'Referral rewards are non-transferable and have no cash equivalent beyond the stated disbursement.',
                  ].map((tc, i) => (
                    <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ color: 'var(--primary)', flexShrink: 0, fontWeight: 700 }}>{i + 1}.</span>
                      <span>{tc}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )
        })()}

        {/* ── ACCOUNT TAB ── */}
        {activeTab === 'account' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Card>
              <SectionLabel>{t('settings.subscription_title')}</SectionLabel>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>{planLabel}</div>
                  {daysLeft !== null && !expired && <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{daysLeft} {t('settings.days_remaining')}</div>}
                  {expired && <div style={{ fontSize: '12px', color: '#DC2626', marginTop: '2px' }}>{t('settings.sub_expired')}</div>}
                  {seller?.subscription_end && !expired && (
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                      {t('settings.sub_renews')} {new Date(seller.subscription_end).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  )}
                </div>
                <button onClick={() => navigate('/pricing')} style={{ fontSize: '12.5px', fontWeight: 600, padding: '7px 16px', borderRadius: 'var(--radius-md)', background: 'var(--primary-light)', color: 'var(--primary)', border: 'none', cursor: 'pointer' }}>
                  {expired || plan === 'trial' ? t('settings.upgrade') : t('settings.change_plan')} {'→'}
                </button>
              </div>
              {plan === 'trial' && !expired && daysLeft !== null && (
                <div>
                  <div style={{ width: '100%', height: '6px', borderRadius: 'var(--radius-sm)', background: '#FDE68A', overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 'var(--radius-sm)', background: '#D97706', width: Math.max(5, (daysLeft / 7) * 100) + '%', transition: 'width 0.3s' }} />
                  </div>
                  <div style={{ fontSize: '11.5px', color: '#D97706', marginTop: '5px' }}>{daysLeft} {t('settings.trial_days')}</div>
                </div>
              )}
              {plan === 'suspended' && (
                <a href="mailto:help@pageconnect.io" style={{ display: 'block', width: '100%', textAlign: 'center', padding: '10px', borderRadius: 'var(--radius-md)', background: '#FEF2F2', color: '#DC2626', fontSize: '13px', fontWeight: 600, textDecoration: 'none' }}>
                  {t('settings.contact_support')} {'→'}
                </a>
              )}
            </Card>

            <Card>
              <SectionLabel>{t('settings.change_password')}</SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <FieldInput label={t('settings.new_password')} value={passwords.newPass} onChange={v => setPasswords(p => ({ ...p, newPass: v }))} placeholder={t('settings.new_password_ph')} type="password" />
                <FieldInput label={t('settings.confirm_password')} value={passwords.confirm} onChange={v => setPasswords(p => ({ ...p, confirm: v }))} placeholder={t('settings.confirm_password_ph')} type="password" />
                {passError && <div style={{ borderRadius: 'var(--radius-md)', padding: '9px 12px', background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', fontSize: '12px' }}>{passError}</div>}
                <button onClick={handleChangePassword} disabled={savingPass} style={{ width: '100%', padding: '11px', borderRadius: 'var(--radius-md)', background: '#DC2626', border: 'none', fontSize: '13px', fontWeight: 700, color: '#fff', cursor: 'pointer', opacity: savingPass ? 0.6 : 1 }}>
                  {savingPass ? t('settings.saving_password') : t('settings.change_password_btn')}
                </button>
              </div>
            </Card>

            <InfoBox color={'var(--primary)'}><strong>Security reminder:</strong> {t('settings.security_info')}</InfoBox>

            <button onClick={() => setShowSignOutConfirm(true)} style={{ width: '100%', padding: '12px', borderRadius: 'var(--radius-md)', background: '#FEF2F2', border: '1px solid #FECACA', fontSize: '13px', fontWeight: 700, color: '#DC2626', cursor: 'pointer' }}>
              {t('settings.sign_out')}
            </button>

            <div style={{ textAlign: 'center', fontSize: '12px', color: 'var(--text-muted)', paddingBottom: '24px' }}>{t('settings.footer')}</div>
          </div>
        )}
      </div>

      {/* Sign out confirm */}
      {showSignOutConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', padding: '24px', maxWidth: '360px', width: '100%', fontFamily: 'var(--font-ui)' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', textAlign: 'center', marginBottom: '8px' }}>{t('settings.sign_out_confirm')}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', marginBottom: '20px' }}>{t('settings.sign_out_desc')}</div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowSignOutConfirm(false)} style={{ flex: 1, padding: '11px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}>{t('settings.sign_out_cancel')}</button>
              <button onClick={handleSignOut} style={{ flex: 1, padding: '11px', borderRadius: 'var(--radius-md)', background: '#DC2626', border: 'none', fontSize: '13px', fontWeight: 700, color: '#fff', cursor: 'pointer' }}>{t('settings.sign_out')}</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes settings-spin { to { transform: rotate(360deg) } }
        @keyframes pc-fade-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .pc-fade-up { animation: pc-fade-up 0.35s ease-out both; }
        .pc-card-hover { transition: transform 0.15s, box-shadow 0.15s; }
        .pc-card-hover:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(11,31,58,0.08) !important; }
        @media(max-width:640px){
          .settings-grid{grid-template-columns:1fr !important}
          .settings-grid > *{grid-column:span 1 !important}
        }
      `}</style>
    </div>
  )
}

export default Settings